/**
 * Cart Modal V2 — substitui o step 1 (lista da sacola) do `openCartModal()` V1.
 *
 * Pega o caminho:
 *   1. Cliente clica "Ver Sacola" (CartBarIsland / BottomNavIsland) ou
 *      pressiona o ícone na bottom nav.
 *   2. V1 `openCartModal()` está sobrescrito pelo bootstrap pra disparar
 *      `aliv:open-cart-modal`. Esta ilha escuta e abre o modal V2.
 *   3. Cliente vê lista de itens, pode editar (delega editCartItem → wizard
 *      simples V2 / pizza+combo V1), remover, ajustar quantidade.
 *   4. Clica "Finalizar Pedido" → ilha fecha + chama o V1 com `currentStep=2`
 *      pulando direto pra tela de endereço/pagamento (V1 ainda dono dos
 *      próximos batches; serão migrados depois).
 *
 * Paridade visual:
 *   - Mobile: fullscreen com header fixo + footer fixo (CTA sticky).
 *   - Desktop: cartão 480px centrado, max-h 92vh, scroll interno.
 *   - safe-area-inset-bottom pro CTA no iPhone.
 *   - Backdrop preto 60%, fade 200ms; sheet slide-up 300ms ease-out spring.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useState } from 'react';
import type { CartLine } from '../types';
import { useModalOpen } from '../hooks/useAnyModalOpen';
import { track, ga4ItemFrom, ga4ItemsFromCart } from '../utils/tracking';
// ComboBlock e ItemDetails vivem em ./_CartItemDetails — compartilhados com
// a sacola do cardápio de mesa (tablet), que precisa exibir o item com
// exatamente o mesmo detalhamento.
import { ItemDetails } from './_CartItemDetails';
import { haptic } from '../utils/haptic';
import { lineTotal as calcLineTotal } from '../utils/lineTotal';
import { t, tr, useI18n } from '../i18n';

declare global {
  interface Window {
    CART?: CartLine[];
    updateCartUI?: () => void;
    editCartItem?: (idx: number) => void;
    removeFromCart?: (idx: number) => void;
    openCartModal?: () => void;
    closeCheckout?: () => void;
    /** V1 controla currentStep do checkout multi-step. */
    currentStep?: number;
    updateCheckoutView?: () => void;
    /** V1 dispara isso após qualquer mutação no CART. */
  }
}

function subscribe(cb: () => void) {
  window.addEventListener('aliv:cart-updated', cb);
  return () => window.removeEventListener('aliv:cart-updated', cb);
}

function getCart(): CartLine[] {
  return window.CART || [];
}

// Snapshot estável (length+sum) pra useSyncExternalStore não loopar.
let lastSig = '';
let lastSnap: CartLine[] = [];
function getStableSnapshot(): CartLine[] {
  const cart = getCart();
  let sig = String(cart.length);
  for (const it of cart) {
    sig += `|${it._tempId}:${it.quantity}:${it.price}`;
  }
  if (sig !== lastSig) {
    lastSig = sig;
    lastSnap = cart.slice(); // shallow copy: items lidos por reference, ok
  }
  return lastSnap;
}

function fmtBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface CartItemRowProps {
  line: CartLine;
  index: number;
  onEdit: (idx: number) => void;
  onRemove: (idx: number) => void;
  onChangeQty: (idx: number, delta: number) => void;
}

/**
 * Bloco de detalhes aninhado por linha — pizza/combo mostra cada
 * personalização em sua própria linha (sabores, tamanho, borda,
 * adicionais com quantidade, ingredientes removidos), igual o V1.
 * Pra produto simples reduz pra 0-2 linhas (só extras/removidos).
 */
/**
 * Renderização paridade EXATA com V1 `renderCheckoutItems`
 * (delivery_app.html:7377). Estrutura:
 *
 *   [QTYx] Nome do item • size_name
 *          ┌─ bloco cinza (1 por sabor) ─────────────┐
 *          │ 1/N  Sabor                              │
 *          │  + adicionalA      + 2x adicionalB      │  ← tags inline
 *          │  Sem ingredienteX  Sem ingredienteY     │
 *          └────────────────────────────────────────┘
 *          🧀 Borda: X    (chip laranja, fora dos sabores)
 *          [Sem A, B]     (só se NÃO for pizza — pizza tem tudo aninhado)
 *          📝 Observação:
 *             texto da obs (bloco amarelo)
 */

function CartItemRow({ line, index, onEdit, onRemove, onChangeQty }: CartItemRowProps) {
  // Inclui adicionais (produto simples e por sabor) — ver utils/lineTotal.
  const lineTotalValue = calcLineTotal(line);
  // Paridade V1 (linha 7521-7524): `nome` + bullet + `size_name` quando
  // o size_name não está embutido no nome. V2 WizardPizza grava
  // `name=sizeName`, então o bullet só aparece se backend antigo gravou
  // diferente (compat com cart legacy).
  const sizeSuffix =
    line.size_name &&
    !line.name.toLowerCase().includes(line.size_name.toLowerCase())
      ? ` • ${line.size_name}`
      : '';

  return (
    <div className="flex items-start gap-3 py-4 border-b border-gray-100 last:border-0">
      {/* Coluna esquerda: thumb numérica (cinza) */}
      <div className="w-10 h-10 shrink-0 rounded-lg bg-gray-100 text-gray-800 flex items-center justify-center font-bold text-sm shadow-sm border border-gray-200 tabular-nums">
        {line.quantity}x
      </div>

      {/* Coluna meio: nome + detalhes + ações */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-gray-900 text-base leading-tight">
            {tr(line.name)}
            {sizeSuffix && (
              <span className="text-gray-500 font-normal text-sm">
                {sizeSuffix}
              </span>
            )}
          </h3>
          <span className="text-base font-extrabold text-gray-900 tabular-nums shrink-0">
            {fmtBRL(lineTotalValue)}
          </span>
        </div>

        <ItemDetails line={line} />

        {/* Ações: stepper + edit + remove */}
        <div className="flex items-center gap-1.5 mt-2">
          <div className="flex items-center bg-gray-100 rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => onChangeQty(index, -1)}
              disabled={line.quantity <= 1}
              className="w-7 h-7 text-red-600 font-bold flex items-center justify-center rounded-l-lg hover:bg-gray-200 transition disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label={t("Diminuir")}
            >
              <i className="fas fa-minus text-[10px]" />
            </button>
            <span className="w-6 text-center font-bold text-gray-800 text-xs tabular-nums">
              {line.quantity}
            </span>
            <button
              type="button"
              onClick={() => onChangeQty(index, 1)}
              disabled={line.quantity >= 99}
              className="w-7 h-7 text-red-600 font-bold flex items-center justify-center rounded-r-lg hover:bg-gray-200 transition disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label={t("Aumentar")}
            >
              <i className="fas fa-plus text-[10px]" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => onEdit(index)}
            className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center transition border border-blue-100"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label={t("Editar item")}
            title={t("Editar")}
          >
            <i className="fas fa-pen text-[10px]" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="w-7 h-7 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 flex items-center justify-center transition border border-red-100"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label={t("Remover item")}
            title={t("Remover")}
          >
            <i className="fas fa-trash-alt text-[10px]" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function CartModalIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [visible, setVisible] = useState(false);
  const [confirmIdx, setConfirmIdx] = useState<number | null>(null);
  // Submit direto mesa/comanda — sem checkout. Tracking de estado pra
  // mostrar spinner no botão Finalizar + modal de sucesso.
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Resgate da comanda por telefone (caso Barbosa): quando a trava devolve
  // COMANDA_BUSY mas o pedido ativo TEM telefone, o cliente pode provar que a
  // comanda é dele digitando o telefone usado nela — em vez do beco "chame o
  // garçom" (que barrava o PRÓPRIO dono quando o iPhone abria outro
  // container de navegador, sem token/device_id).
  const [claimAsk, setClaimAsk] = useState(false);
  const [claimPhone, setClaimPhone] = useState('');
  const [successOpen, setSuccessOpen] = useState<{ isComanda: boolean; isAnonymous: boolean } | null>(null);
  const cart = useSyncExternalStore(subscribe, getStableSnapshot, getStableSnapshot);

  useEffect(() => {
    function onOpen() {
      // Se carrinho vazio, V2 não abre (mesma UX do V1 que dispara alert).
      // Mas o V1 já valida; aqui é defesa: se chegou aqui sem itens, ignora.
      if (!window.CART || window.CART.length === 0) return;
      requestAnimationFrame(() => setVisible(true));
      // GTM/Meta: view_cart (paridade V1 linha 7191).
      const items = ga4ItemsFromCart();
      const value = items.reduce(
        (acc, it) => acc + (Number(it.price) || 0) * (Number(it.quantity) || 0),
        0,
      );
      track('view_cart', {
        currency: 'BRL',
        value,
        ecommerce: { currency: 'BRL', value, items },
      });
    }
    function onForceClose() {
      setVisible(false);
    }
    window.addEventListener('aliv:open-cart-modal', onOpen);
    window.addEventListener('aliv:close-cart-modal', onForceClose);
    return () => {
      window.removeEventListener('aliv:open-cart-modal', onOpen);
      window.removeEventListener('aliv:close-cart-modal', onForceClose);
    };
  }, []);

  // ESC fecha
  useEffect(() => {
    if (!visible) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Lock scroll body
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  // Refcount global pra esconder CartBar / BottomNav enquanto modal aberto.
  useModalOpen(visible, close);

  // Se enquanto modal aberto o carrinho ficar vazio (removeu o último item),
  // fecha sozinho — paridade V1 (`removeFromCart` chama `closeCheckout()` se
  // ficar zerado).
  useEffect(() => {
    if (visible && cart.length === 0) {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length, visible]);

  function close() {
    setVisible(false);
  }

  // Inclui adicionais — ver utils/lineTotal (o backend cobra pela mesma regra).
  const subtotal = useMemo(
    () => cart.reduce((acc, l) => acc + calcLineTotal(l), 0),
    [cart],
  );
  const itemCount = useMemo(
    () => cart.reduce((acc, l) => acc + (Number(l.quantity) || 0), 0),
    [cart],
  );

  function handleEdit(idx: number) {
    // Bootstrap já tem shim que delega: simples → wizard V2; pizza/combo → V1.
    // V1 `editCartItem` antigo abria o modal product-modal (V1 wizard step 4).
    // Pra pizza/combo a animação combinada (cart fechando + wizard abrindo) é
    // melhor experiência se a gente fechar o cart antes.
    close();
    setTimeout(() => {
      window.editCartItem?.(idx);
    }, 280);
  }

  function handleRemove(idx: number) {
    // Abre confirm React próprio (sem SweetAlert/confirm nativo).
    setConfirmIdx(idx);
  }

  function confirmRemove() {
    const idx = confirmIdx;
    if (idx == null) return;
    const cartArr = window.CART;
    if (!cartArr || !cartArr[idx]) { setConfirmIdx(null); return; }
    haptic('tick');
    const line = cartArr[idx];
    // GTM/Meta: remove_from_cart (paridade V1 linha 7921).
    try {
      const lineValue = calcLineTotal(line);
      track('remove_from_cart', {
        currency: 'BRL',
        value: lineValue,
        ecommerce: { currency: 'BRL', value: lineValue, items: [ga4ItemFrom(line)] },
      });
    } catch { /* */ }
    cartArr.splice(idx, 1);
    try { localStorage.setItem('wabiz_cart', JSON.stringify(cartArr)); } catch { /* */ }
    window.updateCartUI?.();
    setConfirmIdx(null);
  }

  function handleChangeQty(idx: number, delta: number) {
    const cart = window.CART;
    if (!cart || !cart[idx]) return;
    const newQty = Math.max(1, Math.min(99, (cart[idx].quantity || 1) + delta));
    if (newQty === cart[idx].quantity) return;
    cart[idx].quantity = newQty;
    try {
      localStorage.setItem('wabiz_cart', JSON.stringify(cart));
    } catch { /* quota */ }
    window.updateCartUI?.();
  }

  function handleFinalize() {
    // Fluxo: Cart V2 → Upsell V2 → Checkout V2 (single-page).
    // O `aliv:upsell-done` é capturado pelo bootstrap (index.tsx) que
    // dispatcha `aliv:open-checkout` pra abrir o CheckoutIsland V2. Não
    // precisamos mais abrir o `#checkout-modal` V1 (sidebar 480px do
    // desktop) — V2 cuida do checkout inteiro agora. Antes esse handler
    // chamava `__V1_OPEN_CART_MODAL__` + `nextCheckoutStep`, o que deixava
    // a sidebar V1 ATRÁS do checkout V2 — visível depois que o V2 fechava
    // (bug "carrinho V1 fica no canto direito").
    //
    // Defesa: garante que o `#checkout-modal` V1 não está visível por
    // outro caminho órfão (qualquer código legado que dê
    // `classList.remove('hidden')` antes do V2 estar pronto).
    try {
      document.getElementById('checkout-modal')?.classList.add('hidden');
    } catch {
      /* */
    }

    // Modo mesa/comanda: SALVA DIRETO sem upsell/checkout. Cliente em
    // mesa só adiciona itens à conta da mesa/comanda — não tem endereço
    // nem pagamento (cobra com garçom no fim). Paridade V1
    // finalizeOrderTableDirect (delivery_app.html:10596).
    const sm = (window as Window & {
      STORE_MODE?: { type?: string; table?: string; comanda?: string };
    }).STORE_MODE;
    const isMesaFlow = sm && (sm.type === 'table_service' || sm.type === 'comanda');
    if (isMesaFlow) {
      void submitMesaDirect();
      return;
    }

    close();
    setTimeout(() => {
      const cartNames = (window.CART || []).map((c) => c.name);
      window.dispatchEvent(
        new CustomEvent('aliv:open-upsell-modal', { detail: { cartNames } }),
      );
    }, 240);
  }

  // Submit direto pra mesa/comanda. Sem checkout, sem upsell, sem
  // endereço/pagamento. Após salvar: limpa cart + abre modal de sucesso.
  // Anônimo recebe isca pra criar conta (ganhar pontos).
  async function submitMesaDirect(claimPhoneArg?: string) {
    if (submitState === 'submitting') return;
    // Mesa/comanda também não manda pedido sem item (mesma trava do checkout;
    // o backend rejeita com EMPTY_CART, mas o cliente merece o aviso na hora).
    if (!window.CART || window.CART.length === 0) {
      setSubmitError('Seu carrinho está vazio. Escolha os itens antes de enviar.');
      return;
    }
    setSubmitState('submitting');
    setSubmitError(null);
    try {
      const sm = (window as Window & {
        STORE_MODE?: { type?: string; table?: string; comanda?: string };
      }).STORE_MODE!;
      const isComanda = sm.type === 'comanda';
      const slug = (window as Window & { STORE_SLUG?: string }).STORE_SLUG || '';
      const u = (window as Window & { USER?: { name?: string; phone?: string } | null }).USER;
      let cname = u?.name?.trim() || '';
      try {
        if (!cname) cname = (sessionStorage.getItem('aliv_guest_name') || '').trim();
      } catch { /* */ }
      const tableNum = String(sm.table || '').trim();
      const comandaNum = String(sm.comanda || '').trim();
      if (isComanda) {
        if (cname) cname = `${cname} Comanda ${comandaNum}`.trim();
        else cname = comandaNum ? `Comanda ${comandaNum}` : 'Cliente Comanda (App)';
        if (tableNum) cname += ` (Mesa ${tableNum})`;
      } else if (!cname) {
        cname = tableNum ? `Mesa ${tableNum}` : 'Cliente Mesa (App)';
      }
      // Adicionais entram: este total vai no FormData do pedido.
      const total = (window.CART || []).reduce((acc, l) => acc + calcLineTotal(l), 0);
      let deviceId = '';
      // RG do aparelho SEMPRE existe no fluxo mesa/comanda (08/08/26, caso
      // Barbosa): antes só o banner de mesa criava o id — pedido nascia sem
      // device_id no banco e a trava anti-intruso barrava o PRÓPRIO cliente
      // na 2ª rodada. Gera aqui se faltar.
      try {
        deviceId = localStorage.getItem('aliv_device_id') || '';
        if (!deviceId) {
          deviceId = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          localStorage.setItem('aliv_device_id', deviceId);
        }
      } catch { /* */ }
      let tokenStr = '';
      try { tokenStr = localStorage.getItem('wabiz_token') || ''; } catch { /* */ }

      const fd = new FormData();
      fd.append('store_slug', slug);
      fd.append('customer_phone', u?.phone || '');
      fd.append('customer_name', cname);
      fd.append('delivery_type', 'mesa');
      if (tableNum) fd.append('table_number', tableNum);
      if (isComanda && comandaNum) fd.append('comanda_number', comandaNum);
      fd.append('payment_method', 'Em Aberto');
      fd.append('items_json', JSON.stringify(window.CART || []));
      fd.append('total_value', String(total.toFixed(2)));
      fd.append('delivery_fee', '0');
      fd.append('discount', '0');
      fd.append('force_status', 'PREPARO');
      if (deviceId) fd.append('device_id', deviceId);
      if (claimPhoneArg && claimPhoneArg.trim()) fd.append('claim_phone', claimPhoneArg.trim());
      fd.append('idempotency_key', `${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

      const res = await fetch('/api/delivery/orders/save', {
        method: 'POST',
        body: fd,
        headers: tokenStr ? { Authorization: `Bearer ${tokenStr}` } : undefined,
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        message?: string;
        error_code?: string;
        can_claim?: boolean;
        order_id?: number | string;
        wabiz_id?: string;
      };
      // Comanda/mesa em uso por outro cliente: backend retorna 409 com
      // error_code claro. Cliente vê mensagem específica em vez de
      // erro genérico. Não limpa cart — cliente pode trocar de comanda.
      if (res.status === 409 && (data.error_code === 'COMANDA_BUSY' || data.error_code === 'TABLE_BUSY')) {
        setSubmitState('error');
        if (data.can_claim) {
          // Oferece o resgate: mesma pessoa em outro navegador/container.
          setClaimAsk(true);
          setSubmitError(claimPhoneArg
            ? t('Telefone não confere com o desta comanda. Confira e tente de novo, ou chame o garçom.')
            : t('Essa comanda já tem pedido em andamento. Se ela é SUA, digite abaixo o telefone usado nela pra continuar.'));
        } else {
          setClaimAsk(false);
          setSubmitError(data.message || 'Comanda/mesa em uso por outro cliente.');
        }
        haptic('error');
        return;
      }
      setClaimAsk(false);
      setClaimPhone('');
      if (!res.ok || data.success === false) {
        // Loja exige mesa na comanda e o pedido veio sem: além da mensagem,
        // reabre o modal de mesa (o mesmo do banner) pro cliente resolver ali.
        if (data.error_code === 'TABLE_REQUIRED') {
          try { window.promptChangeTable?.(); } catch { /* */ }
        }
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      // Cleanup cart
      try {
        if (Array.isArray(window.CART)) window.CART.length = 0;
        else window.CART = [];
        localStorage.removeItem('wabiz_cart');
        window.dispatchEvent(new CustomEvent('aliv:cart-updated'));
        window.updateCartUI?.();
      } catch { /* */ }
      haptic('success');
      setSubmitState('idle');
      setSuccessOpen({ isComanda, isAnonymous: !u?.phone });
      close();
    } catch (err) {
      console.warn('[v2 cart] submitMesaDirect erro', err);
      setSubmitState('error');
      setSubmitError(err instanceof Error ? err.message : t('Erro ao lançar pedido.'));
    }
  }

  // Modal de sucesso pós-pedido mesa/comanda — fica MOUNTED mesmo
  // depois do cart fechar (cart só desmonta o react component, esse
  // modal vive separado renderizado direto pro body).
  const successPortal = successOpen
    ? createPortal(
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-4 bg-black/70"
          style={{ zIndex: 2147483646 }}
        >
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
              <i className="fas fa-check text-3xl" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-1">
              {successOpen.isComanda ? t('Lançado na Comanda!') : t('Pedido na Mesa!')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('Seu pedido já foi enviado pra cozinha.')}
            </p>
            {successOpen.isAnonymous ? (
              <>
                {/* Isca pra cliente anônimo criar conta — paridade V1
                    finalizeOrderTableDirect:10620 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                  <i className="fas fa-gift text-2xl text-yellow-500 mb-1" />
                  <p className="font-bold text-yellow-800 text-sm">{t('Não perca seus pontos!')}</p>
                  <p className="text-xs text-yellow-700 mt-1 leading-snug">
                    {t('Identifique-se rapidinho e acumule pontos neste pedido pra trocar por prêmios.')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSuccessOpen(null);
                    window.dispatchEvent(new CustomEvent('aliv:open-login-modal'));
                  }}
                  className="w-full py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-sm shadow-lg mb-2"
                >
                  <i className="fas fa-star mr-1" /> {t('Quero meus pontos!')}
                </button>
                <button
                  type="button"
                  onClick={() => setSuccessOpen(null)}
                  className="w-full py-2.5 rounded-xl text-gray-500 font-bold text-sm hover:bg-gray-50"
                >
                  {t('Agora não')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setSuccessOpen(null);
                  window.dispatchEvent(
                    new CustomEvent('aliv:open-user-area', { detail: { tab: 'orders' } }),
                  );
                }}
                className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow-lg"
              >
                {t('Ver meus pedidos')}
              </button>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  if (!visible) return successPortal;

  const cartPortal = createPortal(
    <div
      className={[
        'fixed inset-0 z-[78] flex items-end sm:items-center justify-center',
        'bg-black/60',
        'transition-opacity duration-200 ease-out',
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ].join(' ')}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={t("Sua sacola")}
    >
      <div
        className={[
          'relative w-full sm:w-[480px] sm:max-w-[480px]',
          'bg-white sm:rounded-2xl rounded-t-2xl',
          'shadow-[0_-8px_40px_rgba(0,0,0,0.25)] sm:shadow-2xl',
          'overflow-hidden max-h-[92vh] sm:max-h-[88vh]',
          'flex flex-col',
          'transition-transform duration-300',
          visible
            ? 'translate-y-0 sm:scale-100'
            : 'translate-y-full sm:translate-y-4 sm:scale-95',
        ].join(' ')}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle mobile */}
        <div className="sm:hidden pt-2 pb-1 flex justify-center shrink-0">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="px-5 pt-3 pb-3 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-2">
            <i className="fas fa-shopping-bag text-red-600 text-lg" />
            <h2 className="text-base font-extrabold text-gray-900 leading-none">
              {t('Sua Sacola')}
            </h2>
            <span className="text-[11px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full tabular-nums">
              {itemCount} {itemCount === 1 ? 'item' : 'itens'}
            </span>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t("Fechar")}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <i className="fas fa-times text-sm" />
          </button>
        </div>

        {/* Lista de items */}
        <div className="px-5 overflow-y-auto flex-1 min-h-0">
          {cart.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <i className="fas fa-shopping-bag text-4xl text-gray-300 mb-3" />
              <p className="text-sm text-gray-500 font-medium">{t('Sua sacola está vazia')}</p>
            </div>
          ) : (
            cart.map((line, idx) => (
              <CartItemRow
                key={line._tempId || idx}
                line={line}
                index={idx}
                onEdit={handleEdit}
                onRemove={handleRemove}
                onChangeQty={handleChangeQty}
              />
            ))
          )}
        </div>

        {/* Footer com total + CTA */}
        <div
          className="px-5 pt-3 pb-3 border-t border-gray-100 bg-white shrink-0"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              {t('Subtotal')}
            </span>
            <span className="text-lg font-extrabold text-gray-900 tabular-nums">
              {fmtBRL(subtotal)}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mb-2 text-center">
            {(() => {
              const sm = (window as Window & {
                STORE_MODE?: { type?: string; comanda?: string };
              }).STORE_MODE;
              if (sm?.type === 'comanda') return t('Será lançado direto na sua comanda');
              if (sm?.type === 'table_service') return t('Será enviado direto pra cozinha');
              return t('Taxa de entrega e desconto calculados no próximo passo');
            })()}
          </p>
          {submitError && (
            <p className="text-[12px] text-red-600 font-bold mb-2 text-center">
              {submitError}
            </p>
          )}
          {claimAsk && (
            <div className="mb-2 flex gap-2">
              <input
                type="tel"
                inputMode="numeric"
                value={claimPhone}
                onChange={(e) => setClaimPhone(e.target.value)}
                placeholder={t("Telefone usado na comanda")}
                className="flex-1 border border-red-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <button
                type="button"
                onClick={() => { if (claimPhone.replace(/\D/g, '').length >= 8) void submitMesaDirect(claimPhone); }}
                disabled={claimPhone.replace(/\D/g, '').length < 8 || submitState === 'submitting'}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 rounded-xl text-sm disabled:opacity-40"
              >
                {t('É minha')}
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={handleFinalize}
            disabled={cart.length === 0 || submitState === 'submitting'}
            className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3.5 rounded-xl shadow-md active:scale-[0.98] transition flex items-center justify-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {submitState === 'submitting' ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                <span>{t('Enviando...')}</span>
              </>
            ) : (
              <>
                <span>
                  {(() => {
                    const sm = (window as Window & {
                      STORE_MODE?: { type?: string };
                    }).STORE_MODE;
                    if (sm?.type === 'comanda') return t('Lançar na Comanda');
                    if (sm?.type === 'table_service') return t('Enviar pra Cozinha');
                    return t('Finalizar Pedido');
                  })()}
                </span>
                <i className="fas fa-arrow-right" />
              </>
            )}
          </button>
        </div>

        {confirmIdx != null && (() => {
          const line = cart[confirmIdx];
          if (!line) return null;
          return (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-black/50"
              onClick={() => setConfirmIdx(null)}
            >
              <div
                className="bg-white rounded-2xl max-w-xs w-full p-5 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mx-auto w-12 h-12 mb-3 rounded-full bg-red-50 flex items-center justify-center">
                  <i className="fas fa-trash-alt text-xl text-red-600" />
                </div>
                <h3 className="text-base font-extrabold text-gray-900 text-center mb-1">
                  {t('Remover item?')}
                </h3>
                <p className="text-xs text-gray-600 text-center mb-4">
                  <span className="font-bold">{tr(line.name)}</span> {t('será removido da sua sacola.')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmIdx(null)}
                    className="flex-1 py-2.5 rounded-xl text-gray-700 font-bold text-sm border border-gray-200 hover:bg-gray-50"
                  >
                    {t('Cancelar')}
                  </button>
                  <button
                    type="button"
                    onClick={confirmRemove}
                    className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
                  >
                    {t('Remover')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      {cartPortal}
      {successPortal}
    </>
  );
}
